import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import StoreIcon from "@material-ui/icons/Store";
import StorefrontIcon from "@material-ui/icons/Storefront";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import BlockIcon from "@material-ui/icons/Block";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import DeleteIcon from "@material-ui/icons/Delete";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { Stars, assetUrl, readableError } from "../shopFields";
import "../superShops.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const SHOP_FILTERS = `
  _id: $_id
  name: $name
  shopAdminName: $shopAdminName
  state: $state
  active: $active
  verified: $verified
  budget: $budget
  rootCategory: $rootCategory
  attributes: $attributes
  averageRateFrom: $averageRateFrom
  averageRate: $averageRate
`;

const SHOP_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $name: String
  $shopAdminName: String
  $state: ShopStateInput
  $active: Boolean
  $verified: Boolean
  $budget: String
  $rootCategory: ID
  $attributes: [ID]
  $averageRateFrom: Float
  $averageRate: Float
  $averageRateSort: Int
  $createdAtSort: Int
`;

const GET_SHOPS = gql`
  query getShopsByAdmin(${SHOP_VARIABLES}) {
    getShopsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${SHOP_FILTERS} }
      sort: { averageRate: $averageRateSort, createdAt: $createdAtSort }
    ) {
      _id
      name
      logoUrl
      averageRate
      active
      verified
      isRejected
      state
      budget
      phoneNumbers
      shopAdmin {
        _id
        fullName
        email
        phoneNumber
      }
      rootCategory {
        _id
        title
      }
      categories {
        _id
        title
      }
      attributes {
        _id
        name
      }
    }
    getShopsByAdminCount(filters: { ${SHOP_FILTERS} })
  }
`;

// Platform totals rather than a count of the page on screen.
const GET_SHOP_COUNTS = gql`
  query getShopSummaryCounts {
    total: getShopsByAdminCount(filters: {})
    open: getShopsByAdminCount(filters: { active: true })
    unverified: getShopsByAdminCount(filters: { verified: false })
    suspended: getShopsByAdminCount(filters: { state: SUSPENDED })
  }
`;

const GET_LOOKUPS = gql`
  query getShopLookups {
    getAttributes {
      _id
      name
    }
    getCategories(filters: { parent: null }) {
      _id
      title
    }
  }
`;

const SUSPEND_SHOPS = gql`
  mutation suspendShopByAdmin($idSet: [ID!]!) {
    suspendShopByAdmin(idSet: $idSet) {
      _id
      state
    }
  }
`;

const DELETE_SHOPS = gql`
  mutation deleteShopByAdmin($idSet: [ID!]!) {
    deleteShopByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATE_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
];
const STATE_LABELS = STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const OPEN_OPTIONS = [
  { value: "", label: "Open and closed" },
  { value: "true", label: "Open now" },
  { value: "false", label: "Closed now" },
];
const OPEN_LABELS = OPEN_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const VERIFIED_OPTIONS = [
  { value: "", label: "Any verification" },
  { value: "true", label: "Verified" },
  { value: "false", label: "Not verified" },
];
const VERIFIED_LABELS = VERIFIED_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const BUDGET_OPTIONS = [
  { value: "B", label: "$" },
  { value: "BB", label: "$$" },
  { value: "BBB", label: "$$$" },
];
const BUDGET_LABELS = { B: "$", BB: "$$", BBB: "$$$" };

const SEARCH_TARGETS = [
  { value: "name", label: "Shop name" },
  { value: "shopAdminName", label: "Owner name" },
];
const SEARCH_LABELS = { name: "Shop name", shopAdminName: "Owner name" };

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "rateHigh", label: "Best rated" },
  { value: "rateLow", label: "Worst rated" },
];

const FILTER_DEBOUNCE_MS = 350;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Restaurants = () => {
  const [searchTarget, setSearchTarget] = useState("name");
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [openFilter, setOpenFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [budgetFilter, setBudgetFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [attributeFilter, setAttributeFilter] = useState("");
  const [rateFrom, setRateFrom] = useState("");
  const [rateTo, setRateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({ limit: perPage, skip: 0, createdAtSort: -1 });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      setVariables((v) => ({
        ...v,
        skip: 0,
        name: undefined,
        shopAdminName: undefined,
        ...(term ? { [searchTarget]: term } : {}),
        state: stateFilter || undefined,
        active: openFilter === "" ? undefined : openFilter === "true",
        verified: verifiedFilter === "" ? undefined : verifiedFilter === "true",
        budget: budgetFilter || undefined,
        rootCategory: categoryFilter || undefined,
        attributes: attributeFilter ? [attributeFilter] : undefined,
        averageRateFrom: rateFrom ? parseFloat(rateFrom) : undefined,
        averageRate: rateTo ? parseFloat(rateTo) : undefined,
        averageRateSort: sortOption === "rateHigh" ? -1 : sortOption === "rateLow" ? 1 : undefined,
        createdAtSort: sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [
    searchTarget,
    searchTerm,
    stateFilter,
    openFilter,
    verifiedFilter,
    budgetFilter,
    categoryFilter,
    attributeFilter,
    rateFrom,
    rateTo,
    sortOption,
  ]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_SHOPS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_SHOP_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const { data: lookupData } = useQuery(GET_LOOKUPS, { errorPolicy: "all" });
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_SHOPS, {
    fetchPolicy: "network-only",
  });
  const [suspendShops] = useMutation(SUSPEND_SHOPS);
  const [deleteShops] = useMutation(DELETE_SHOPS);

  const shops = (data && data.getShopsByAdmin) || [];
  const totalCount = (data && data.getShopsByAdminCount) || 0;
  const counts = countsData || {};

  const categoryOptions = useMemo(
    () => ((lookupData && lookupData.getCategories) || []).map((c) => ({ value: c._id, label: c.title })),
    [lookupData]
  );
  const attributeOptions = useMemo(
    () => ((lookupData && lookupData.getAttributes) || []).map((a) => ({ value: a._id, label: a.name })),
    [lookupData]
  );
  const labelFor = (options, value) => {
    const found = options.find((o) => o.value === value);
    return found ? found.label : "-";
  };

  /* ---------------- selection + actions ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = shops.map((s) => s._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    shops.length > 0 && selectedIds.length === shops.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runAction = (mutate, targets, onDone) => {
    setWorking(true);
    setActionError("");
    mutate({ variables: { idSet: targets.map((s) => s._id) } })
      .then(() => {
        setWorking(false);
        onDone();
        setSelectedIds([]);
        refetch();
        refetchCounts();
      })
      .catch((err) => {
        setWorking(false);
        onDone();
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (stateFilter) activeFilterChips.push({ id: "state", label: `State: ${STATE_LABELS[stateFilter]}` });
  if (openFilter) activeFilterChips.push({ id: "open", label: OPEN_LABELS[openFilter] });
  if (verifiedFilter) activeFilterChips.push({ id: "verified", label: VERIFIED_LABELS[verifiedFilter] });
  if (budgetFilter) activeFilterChips.push({ id: "budget", label: `Price: ${BUDGET_LABELS[budgetFilter]}` });
  if (categoryFilter) {
    activeFilterChips.push({ id: "category", label: `Type: ${labelFor(categoryOptions, categoryFilter)}` });
  }
  if (attributeFilter) {
    activeFilterChips.push({
      id: "attribute",
      label: `Cuisine: ${labelFor(attributeOptions, attributeFilter)}`,
    });
  }
  if (rateFrom || rateTo) {
    activeFilterChips.push({ id: "rate", label: `Rating: ${rateFrom || "0"} to ${rateTo || "5"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "state") setStateFilter("");
    if (id === "open") setOpenFilter("");
    if (id === "verified") setVerifiedFilter("");
    if (id === "budget") setBudgetFilter("");
    if (id === "category") setCategoryFilter("");
    if (id === "attribute") setAttributeFilter("");
    if (id === "rate") {
      setRateFrom("");
      setRateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStateFilter("");
    setOpenFilter("");
    setVerifiedFilter("");
    setBudgetFilter("");
    setCategoryFilter("");
    setAttributeFilter("");
    setRateFrom("");
    setRateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      ["Shop", "Type", "Owner", "Owner phone", "Owner email", "Rating", "Price", "State", "Open", "Verified", "Cuisines"],
    ];
    (exportData.getShopsByAdmin || []).forEach((shop) => {
      rows.push([
        shop.name || "-",
        (shop.rootCategory && shop.rootCategory.title) || "-",
        (shop.shopAdmin && shop.shopAdmin.fullName) || "-",
        (shop.shopAdmin && shop.shopAdmin.phoneNumber) || "-",
        (shop.shopAdmin && shop.shopAdmin.email) || "-",
        shop.averageRate ? Number(shop.averageRate).toFixed(1) : "-",
        BUDGET_LABELS[shop.budget] || shop.budget || "-",
        STATE_LABELS[shop.state] || shop.state || "-",
        shop.active ? "open" : "closed",
        shop.verified ? "yes" : "no",
        (shop.attributes || []).map((a) => a.name).join(", ") || "-",
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Shops");
    XLSX.writeFile(wb, "ShopsAndRestaurants.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superShopsPage superShopListPage">
        <main className="main">
          <PageHeader title="Shops and Restaurants" description="Every shop trading on the platform." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedShops = shops.filter((s) => selectedIds.includes(s._id));

  return (
    <div className="page superShopsPage superShopListPage">
      <main className="main">
        <PageHeader
          title="Shops and Restaurants"
          count={totalCount}
          description="Every shop trading on the platform, who owns it, and how it is rated."
          actions={
            <React.Fragment>
              <PageActionButton
                icon={<CloudDownloadIcon fontSize="small" />}
                label={exporting ? "Exporting" : "Export"}
                info={exporting ? "Preparing file" : `${totalCount} shop${totalCount === 1 ? "" : "s"}`}
                onClick={handleExport}
                disabled={totalCount === 0}
                loading={exporting}
              />
              <Button
                variant="primary"
                onClick={() => (window.location.href = `${endPoint}/superAdmin/addShop`)}
              >
                Add shop
              </Button>
            </React.Fragment>
          }
        />

        <section className="stats">
          <MetricCard icon={<StoreIcon />} title="Matching shops" value={totalCount} />
          <MetricCard
            icon={<StorefrontIcon />}
            title="Open now"
            value={counts.open || 0}
            tone={counts.open > 0 ? "success" : "default"}
          />
          <MetricCard
            icon={<CheckCircleOutlineIcon />}
            title="Awaiting verification"
            value={counts.unverified || 0}
            tone={counts.unverified > 0 ? "warning" : "default"}
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Suspended"
            value={counts.suspended || 0}
            tone={counts.suspended > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={stateFilter ? `State: ${STATE_LABELS[stateFilter]}` : "State"}
              options={[{ value: "", label: "Any state" }, ...STATE_OPTIONS]}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            />
            <FilterDropdown
              label={openFilter ? OPEN_LABELS[openFilter] : "Open"}
              options={OPEN_OPTIONS}
              value={openFilter}
              onChange={(e) => setOpenFilter(e.target.value)}
            />
            <FilterDropdown
              label={verifiedFilter ? VERIFIED_LABELS[verifiedFilter] : "Verification"}
              options={VERIFIED_OPTIONS}
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
            />
            <FilterDropdown
              label={categoryFilter ? `Type: ${labelFor(categoryOptions, categoryFilter)}` : "Type"}
              options={[{ value: "", label: "All types" }, ...categoryOptions]}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />
            <FilterDropdown
              label={
                attributeFilter ? `Cuisine: ${labelFor(attributeOptions, attributeFilter)}` : "Cuisine"
              }
              options={[{ value: "", label: "All cuisines" }, ...attributeOptions]}
              value={attributeFilter}
              onChange={(e) => setAttributeFilter(e.target.value)}
            />
            <FilterDropdown
              label={budgetFilter ? `Price: ${BUDGET_LABELS[budgetFilter]}` : "Price"}
              options={[{ value: "", label: "Any price" }, ...BUDGET_OPTIONS]}
              value={budgetFilter}
              onChange={(e) => setBudgetFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Rating"
              fromValue={rateFrom}
              toValue={rateTo}
              onFromChange={(e) => setRateFrom(e.target.value)}
              onToChange={(e) => setRateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <React.Fragment>
              <Button
                variant="secondary"
                size="sm"
                icon={<PauseCircleOutlineIcon fontSize="small" />}
                onClick={() => setSuspendTarget(selectedShops)}
              >
                Suspend
              </Button>
              <Button
                variant="dangerQuiet"
                size="sm"
                icon={<DeleteIcon fontSize="small" />}
                onClick={() => setDeleteTarget(selectedShops)}
              >
                Delete
              </Button>
            </React.Fragment>
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh shops"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superShopsSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every shop on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Shop</div>
              <div className="tableHeaderCell">Owner</div>
              <div className="tableHeaderCell">Cuisines</div>
              <div className="tableHeaderCell">Rating</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {shops.length === 0 ? (
              <EmptyState
                icon={<StoreIcon />}
                title={activeFilterChips.length === 0 ? "No shops yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Shops appear here once owners sign up and are verified."
                    : "No shops match these filters. Try widening or clearing them."
                }
              />
            ) : (
              shops.map((shop) => {
                const logo = assetUrl(shop.logoUrl);
                const cuisines = (shop.attributes || []).map((a) => a.name);
                return (
                  <div className="row" key={shop._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(shop._id)}
                      onChange={() => toggleSelectOne(shop._id)}
                      inputProps={{ "aria-label": `Select ${shop.name}` }}
                    />

                    <div className="superShopCell" data-label="Shop">
                      <span className="superShopLogo">
                        {logo ? <img src={logo} alt="" /> : <StorefrontIcon fontSize="small" />}
                      </span>
                      <span className="superShopText">
                        <span className="superShopName">{shop.name}</span>
                        <span className="superShopMeta">
                          {[
                            shop.rootCategory && shop.rootCategory.title,
                            BUDGET_LABELS[shop.budget],
                            (shop.phoneNumbers || [])[0],
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </div>

                    <div className="superShopPerson" data-label="Owner">
                      {shop.shopAdmin ? (
                        <React.Fragment>
                          <span className="superShopPersonName">{shop.shopAdmin.fullName}</span>
                          <span className="superShopPersonMeta">
                            {shop.shopAdmin.phoneNumber || shop.shopAdmin.email}
                          </span>
                        </React.Fragment>
                      ) : (
                        <span className="superShopMuted">No owner</span>
                      )}
                    </div>

                    <span className="superShopStack" data-label="Cuisines">
                      {cuisines.length ? (
                        <span className="superShopTag">
                          {cuisines.length === 1 ? cuisines[0] : `${cuisines[0]} +${cuisines.length - 1}`}
                        </span>
                      ) : (
                        <span className="superShopMuted">None set</span>
                      )}
                    </span>

                    <span data-label="Rating">
                      <Stars value={shop.averageRate} />
                    </span>

                    <span className="superShopStack" data-label="Status">
                      <StatusBadge
                        status={shop.state === "SUSPENDED" ? "danger" : shop.active ? "success" : "neutral"}
                        label={
                          shop.state === "SUSPENDED" ? "Suspended" : shop.active ? "Open" : "Closed"
                        }
                      />
                      {!shop.verified && (
                        <StatusBadge
                          status={shop.isRejected ? "danger" : "warning"}
                          label={shop.isRejected ? "Rejected" : "Unverified"}
                          dot={false}
                        />
                      )}
                    </span>

                    <div className="superShopActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setSuspendTarget([shop])}
                        title="Suspend shop"
                      >
                        <PauseCircleOutlineIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/${encodeURIComponent(
                            shop.name
                          )}/${shop._id}`)
                        }
                        title="Open shop"
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(suspendTarget)}
        onClose={() => (working ? null : setSuspendTarget(null))}
        title={suspendTarget && suspendTarget.length > 1 ? "Suspend shops" : "Suspend shop"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setSuspendTarget(null), disabled: working },
          {
            label: "Suspend",
            onClick: () => runAction(suspendShops, suspendTarget || [], () => setSuspendTarget(null)),
            variant: "danger",
            loading: working,
          },
        ]}
      >
        <div className="superShopConfirm">
          {suspendTarget && suspendTarget.length > 1
            ? `${suspendTarget.length} shops will stop taking orders and their owners will lose access to the shop panel.`
            : "This shop will stop taking orders and its owner will lose access to the shop panel."}
        </div>
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => (working ? null : setDeleteTarget(null))}
        title={deleteTarget && deleteTarget.length > 1 ? "Delete shops" : "Delete shop"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null), disabled: working },
          {
            label: "Delete",
            onClick: () => runAction(deleteShops, deleteTarget || [], () => setDeleteTarget(null)),
            variant: "danger",
            loading: working,
          },
        ]}
      >
        <div className="superShopConfirm">
          {deleteTarget && deleteTarget.length > 1
            ? `${deleteTarget.length} shops will be removed along with their menus and products. This cannot be undone.`
            : "This shop will be removed along with its menu and products. This cannot be undone."}
        </div>
      </DialogShell>
    </div>
  );
};

export default Restaurants;
